import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Section, Spinner } from "@/components/ui-parts";
import { Sparkles, Loader2, History, Trash2, Eye, ClipboardCheck } from "lucide-react";

function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let listBuffer = null; let listType = null;
  const flushList = () => {
    if (listBuffer) {
      const Tag = listType === "ol" ? "ol" : "ul";
      out.push(<Tag key={out.length}>{listBuffer.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inlineFormat(it) }} />)}</Tag>);
      listBuffer = null; listType = null;
    }
  };
  const inlineFormat = (t) => t
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>');
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^#{3}\s/.test(line)) { flushList(); out.push(<h3 key={out.length}>{line.replace(/^#{3}\s/, "")}</h3>); }
    else if (/^#{2}\s/.test(line)) { flushList(); out.push(<h2 key={out.length}>{line.replace(/^#{2}\s/, "")}</h2>); }
    else if (/^#\s/.test(line)) { flushList(); out.push(<h1 key={out.length}>{line.replace(/^#\s/, "")}</h1>); }
    else if (/^\d+\.\s/.test(line)) {
      if (listType !== "ol") { flushList(); listType = "ol"; listBuffer = []; }
      listBuffer.push(line.replace(/^\d+\.\s/, ""));
    }
    else if (/^[-*]\s/.test(line)) {
      if (listType !== "ul") { flushList(); listType = "ul"; listBuffer = []; }
      listBuffer.push(line.replace(/^[-*]\s/, ""));
    }
    else if (line.trim() === "") { flushList(); }
    else { flushList(); out.push(<p key={out.length} dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />); }
  }
  flushList();
  return out;
}

const PROGRESS_HINTS = [
  { at: 0, text: "Toplama başlıyor: assessment verileri okunuyor..." },
  { at: 8, text: "390 requirement üzerinden gap ve risk sinyalleri toparlanıyor..." },
  { at: 20, text: "Framework crosswalk (ISO / NIST / IEC 62443 / DORA) referansları hazırlanıyor..." },
  { at: 35, text: "Claude Sonnet 4.5 executive öneri paketini oluşturuyor..." },
  { at: 60, text: "Immediate / Strategic / Long-term aksiyonlar yazılıyor..." },
  { at: 90, text: "Framework alignment notları hazırlanıyor..." },
  { at: 120, text: "Sonuç dokümantasyonu son işleme alınıyor. Neredeyse tamam..." },
];

export default function AIRecommendationsPage() {
  const { user } = useAuth();
  const { withScope, activeTenantId } = useTenant();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [focus, setFocus] = useState("executive");
  const [framework, setFramework] = useState("");
  const [frameworks, setFrameworks] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const canDelete = user?.role !== "viewer";

  useEffect(() => {
    api.get("/reference/frameworks").then(({ data }) => setFrameworks(data)).catch(() => {});
  }, []);

  const loadHistory = () => {
    setLoadingHistory(true);
    api.get("/ai/reports", { params: withScope() })
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [activeTenantId]);

  useEffect(() => {
    if (!loading) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);

  const currentHint = [...PROGRESS_HINTS].reverse().find((h) => elapsed >= h.at)?.text || "";

  const run = async () => {
    setLoading(true); setErr(""); setResult(null);
    try {
      const { data: start } = await api.post("/ai/recommendations", { focus, framework: framework || null }, { params: withScope() });
      const jobId = start.job_id;
      const started = Date.now();
      const timeout = 5 * 60 * 1000;
      let done = null;
      while (Date.now() - started < timeout) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: status } = await api.get(`/ai/recommendations/${jobId}`);
          if (status.status === "done") { done = status; break; }
          if (status.status === "error") throw new Error(status.error || "AI job failed");
        } catch (pollErr) {
          if (pollErr.message && pollErr.message.includes("AI job failed")) throw pollErr;
        }
      }
      if (!done) throw new Error("AI job did not complete within 5 minutes. Please try again.");
      setResult({ ...done, generated_at: done.completed_at });
      loadHistory();
    } catch (e) { setErr(e.message || formatApiError(e)); }
    finally { setLoading(false); }
  };

  const openReport = async (jobId) => {
    try {
      const { data } = await api.get(`/ai/recommendations/${jobId}`);
      setResult({ ...data, generated_at: data.completed_at });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { alert(formatApiError(e)); }
  };

  const deleteReport = async (jobId) => {
    if (!window.confirm("Bu AI raporunu silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/ai/reports/${jobId}`);
      loadHistory();
      if (result?.id === jobId) setResult(null);
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="p-8 max-w-[1600px]" data-testid="ai-page">
      <PageHeader eyebrow="Intelligence" title="AI Recommendation Engine"
        description="Claude Sonnet 4.5 analyzes the current assessment and generates board-ready recommendations with framework alignment." />

      <Section title="Configure & Generate">
        <div className="p-5 flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Focus</label>
            <select value={focus} onChange={(e) => setFocus(e.target.value)} data-testid="ai-focus">
              <option value="executive">Executive Recommendations</option>
              <option value="domain">Domain-level Improvements</option>
              <option value="remediation">Immediate Remediation Actions</option>
            </select>
          </div>
          <div className="min-w-[220px]">
            <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Framework Focus (Optional)</label>
            <select value={framework} onChange={(e) => setFramework(e.target.value)} data-testid="ai-framework">
              <option value="">General (all frameworks)</option>
              {frameworks.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" onClick={run} disabled={loading} data-testid="ai-generate">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loading ? `Generating... ${elapsed}s` : "Generate Recommendations"}
          </button>
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">
            Model: <span className="text-[#00E5FF]">claude-sonnet-4.5</span>
            {loading && <span className="text-slate-400 normal-case tracking-normal ml-2">· Async job (may take 60–120s)</span>}
          </div>
        </div>
        {loading && (
          <div className="px-5 pb-5" data-testid="ai-progress-hint">
            <div className="card p-3 border-[#00E5FF]/20 bg-[#00E5FF]/5 flex items-center gap-3">
              <Loader2 size={14} className="animate-spin text-[#00E5FF]" />
              <div className="text-xs text-slate-300 flex-1">{currentHint}</div>
              <div className="text-[10px] font-mono text-slate-500">{elapsed}s</div>
            </div>
          </div>
        )}
      </Section>

      {err && <div className="card p-4 border-red-500/30 text-xs text-red-400 mb-4">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Report area */}
        <div className="lg:col-span-3">
          {result ? (
            <Section
              title={result.framework_name ? `AI Recommendations · ${result.framework_name}` : "AI Recommendations"}
              description={`ECRI ${result.ecri} · Maturity ${result.maturity} · ${new Date(result.generated_at || result.completed_at).toLocaleString()}`}
            >
              <div className="p-6 md" data-testid="ai-output">
                {renderMarkdown(result.recommendations)}
              </div>
            </Section>
          ) : (
            !loading && (
              <div className="card p-8 text-center">
                <Sparkles size={28} className="mx-auto text-[#00E5FF] mb-3" strokeWidth={1.5} />
                <div className="text-sm text-slate-300 mb-1 font-semibold">Ready for AI Analysis</div>
                <div className="text-xs text-slate-500 max-w-md mx-auto">
                  Click Generate to have Claude analyze your current GACRF assessment and produce executive-level,
                  framework-aligned recommendations. Or open a past report from the history panel →
                </div>
              </div>
            )
          )}
        </div>

        {/* History sidebar */}
        <aside className="lg:col-span-1">
          <div className="card sticky top-4">
            <div className="px-4 py-3 border-b border-slate-800/70 flex items-center gap-2">
              <History size={14} className="text-slate-400" />
              <div className="font-heading text-sm font-semibold">Report History</div>
              <div className="ml-auto text-[10px] font-mono text-slate-500">{history.length}</div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto" data-testid="ai-history">
              {loadingHistory ? (
                <div className="p-4"><Spinner label="Loading..." /></div>
              ) : history.length === 0 ? (
                <div className="p-4 text-xs text-slate-500 text-center">Henüz kayıtlı AI raporu yok.</div>
              ) : (
                history.map((r) => (
                  <div key={r.id} className={`px-3 py-2.5 border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors ${result?.id === r.id ? "bg-[#00E5FF]/5 border-l-2 border-l-[#00E5FF]" : ""}`} data-testid={`ai-history-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-400">
                        <ClipboardCheck size={10} />
                        {r.framework_name || "General"}
                      </div>
                      <div className="flex items-center gap-1">
                        <button className="btn btn-ghost btn-sm p-1" onClick={() => openReport(r.id)} data-testid={`open-report-${r.id}`} title="Open">
                          <Eye size={11} />
                        </button>
                        {canDelete && (
                          <button className="btn btn-ghost btn-sm p-1 hover:text-red-400" onClick={() => deleteReport(r.id)} data-testid={`del-report-${r.id}`} title="Delete">
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-slate-200">
                      {r.focus === "executive" ? "Executive Recommendations" : r.focus === "domain" ? "Domain-level Improvements" : "Remediation Actions"}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                      ECRI {r.ecri} · {r.maturity}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      {new Date(r.completed_at || r.started_at).toLocaleString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
